import React, { useEffect, useRef, useState } from "react";
import { Send, Paperclip, X, Loader2, FileText } from "lucide-react";
import { INK, INK_3, LINE, BURGUNDY } from "../../Bureau/bureauTokens";
import { uploadFileToBlob } from "../../../utils/uploadFileToBlob";

function formatFileSize(bytes) {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function MessageComposer({ onSend }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  // null tant qu'aucun envoi n'est en cours ; 0-100 pendant l'upload direct
  // vers Blob — un gros fichier peut prendre plusieurs secondes/minutes selon
  // sa taille et la connexion, l'utilisateur doit voir que ça avance.
  const [uploadProgress, setUploadProgress] = useState(null);
  const fileInputRef = useRef(null);

  // Aperçu image via object URL — révoqué à chaque changement/démontage
  // pour ne pas accumuler de références mémoire.
  useEffect(() => {
    if (!attachment || !attachment.type.startsWith("image/")) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(attachment);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [attachment]);

  const clearAttachment = () => setAttachment(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permet de re-choisir le même fichier juste après
    if (!file) return;
    setAttachment(file);
  };

  const submit = async () => {
    const body = text.trim();
    if ((!body && !attachment) || sending || uploadProgress !== null) return;
    setSending(true);
    try {
      let fileMeta = null;
      if (attachment) {
        // Upload direct navigateur → Blob (contourne la limite 4,5 Mo des
        // Vercel Functions) : seule la référence (URL) part vers l'API.
        setUploadProgress(0);
        const url = await uploadFileToBlob(
          attachment,
          "https://backend-myalfa.vercel.app/api/discussion/upload-token",
          ({ percentage }) => setUploadProgress(Math.round(percentage)),
        );
        fileMeta = { url, name: attachment.name, type: attachment.type, size: attachment.size };
      }
      await onSend(body, fileMeta);
      setText("");
      setAttachment(null);
    } finally {
      setSending(false);
      setUploadProgress(null);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const canSend = (!!text.trim() || !!attachment) && !sending && uploadProgress === null;

  return (
    <div className="flex-shrink-0" style={{ borderTop: `1px solid ${LINE}` }}>
      {(attachment || uploadProgress !== null) && (
        <div className="flex items-center gap-2 px-4 pt-3">
          {uploadProgress !== null ? (
            <div className="flex items-center gap-2 text-[11.5px]" style={{ color: INK_3 }}>
              <Loader2 size={13} className="animate-spin" /> Envoi du fichier… {uploadProgress}%
            </div>
          ) : (
            <div
              className="flex items-center gap-2 pl-1.5 pr-2 py-1.5"
              style={{ background: "#f8fafc", border: `1px solid ${LINE}`, borderRadius: 10 }}
            >
              {previewUrl ? (
                <img src={previewUrl} alt={attachment.name} style={{ width: 32, height: 32, borderRadius: 6, objectFit: "cover" }} />
              ) : (
                <span
                  className="flex items-center justify-center flex-shrink-0"
                  style={{ width: 32, height: 32, borderRadius: 6, background: "#eef2f7", color: INK_3 }}
                >
                  <FileText size={15} />
                </span>
              )}
              <div className="min-w-0">
                <p className="text-[11.5px] font-medium truncate m-0" style={{ color: INK, maxWidth: 200 }}>
                  {attachment.name}
                </p>
                <p className="text-[10px] m-0" style={{ color: INK_3 }}>
                  {formatFileSize(attachment.size)}
                </p>
              </div>
              <button onClick={clearAttachment} className="flex-shrink-0" style={{ color: INK_3 }}>
                <X size={13} />
              </button>
            </div>
          )}
        </div>
      )}

      <div className="flex items-end gap-2 px-4 py-3">
        <input ref={fileInputRef} type="file" onChange={handleFileChange} className="hidden" />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadProgress !== null}
          className="flex-shrink-0 flex items-center justify-center disabled:opacity-50"
          style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${LINE}`, color: INK_3 }}
        >
          <Paperclip size={15} />
        </button>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Écrivez un message… (Entrée pour envoyer, Maj+Entrée pour un saut de ligne)"
          rows={1}
          className="flex-1 px-3 py-2 text-xs outline-none resize-none"
          style={{ border: `1px solid ${LINE}`, borderRadius: 10, color: INK, maxHeight: 120 }}
        />
        <button
          onClick={submit}
          disabled={!canSend}
          className="flex-shrink-0 flex items-center justify-center disabled:opacity-50"
          style={{ width: 36, height: 36, borderRadius: 10, background: BURGUNDY, color: "#fff" }}
        >
          <Send size={14} />
        </button>
      </div>
    </div>
  );
}

export default MessageComposer;
